import React, { useEffect, useId, useState } from "react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function FormDisclosure({ title, children, className = "", open: controlledOpen, onOpenChange }) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const changeOpen = onOpenChange || setUncontrolledOpen;

  return <section className={`space-y-3 ${className}`}>
    <Button
      type="button"
      variant="outline"
      className="w-full justify-between"
      aria-expanded={open}
      onClick={() => changeOpen(!open)}
    >
      <span className="flex items-center gap-2">
        {!open && <Plus aria-hidden="true" />}
        {open ? `Close ${title}` : title}
      </span>
      {open ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
    </Button>
    {open && <div className="space-y-3">{children}</div>}
  </section>;
}

export function LabeledSelect({ label, children, className = "", showLabel = true, ...props }) {
  const options = [];
  const getOptionLabel = (children) => {
    const parts = [];
    const collectText = (nodes) => React.Children.forEach(nodes, (node) => {
      if (typeof node === "string" || typeof node === "number") {
        parts.push(String(node));
      } else if (React.isValidElement(node)) {
        collectText(node.props.children);
      }
    });
    collectText(children);
    return parts.join("").replace(/\s+/g, " ").trim();
  };
  const collectOptions = (nodes) => React.Children.forEach(nodes, (node) => {
    if (!React.isValidElement(node)) return;
    if (node.type === React.Fragment) {
      collectOptions(node.props.children);
      return;
    }
    if (node.type !== "option") return;
    const optionLabel = getOptionLabel(node.props.children);
    const optionValue = node.props.value ?? optionLabel;
    options.push({ value: String(optionValue), label: optionLabel });
  });
  collectOptions(children);

  const placeholderOption = options.find((option) => option.value === "");
  const searchableOptions = options.filter((option) => option.value !== "");
  const { value, onChange, disabled, required, "aria-label": ariaLabel, className: controlClassName = "", ...otherProps } = props;
  const listId = `hospital-options-${useId().replaceAll(":", "")}`;
  const selectedLabel = searchableOptions.find((option) => option.value === String(value ?? ""))?.label || "";
  const [inputValue, setInputValue] = useState(selectedLabel);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setInputValue(selectedLabel);
  }, [focused, selectedLabel]);

  const handleFocus = () => {
    setFocused(true);
    if (selectedLabel && inputValue === selectedLabel) {
      setInputValue("");
    }
  };

  const handleBlur = () => {
    setFocused(false);
    setInputValue(selectedLabel);
  };

  const handleChange = (event) => {
    const nextText = event.target.value;
    const match = searchableOptions.find((option) => option.label.toLocaleLowerCase() === nextText.trim().toLocaleLowerCase());
    setInputValue(nextText);
    onChange?.({ target: { value: match?.value || "" } });
  };

  return <fieldset className={`relative m-0 h-12 min-w-0 rounded-md border border-input bg-background px-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 ${className}`}>
    <legend className={showLabel ? "ml-1 max-w-[88%] truncate px-1 text-[11px] font-semibold leading-none text-foreground" : "sr-only"}>{label}</legend>
    <input
      {...otherProps}
      list={listId}
      value={inputValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholderOption?.label || `Select ${label.toLowerCase()}`}
      aria-label={ariaLabel || label}
      aria-required={required || undefined}
      required={required}
      disabled={disabled}
      data-notched-input="true"
      data-testid={otherProps["data-testid"]}
      className={`h-8 w-full border-0 bg-transparent px-1 py-0 text-sm text-foreground shadow-none outline-none placeholder:text-muted-foreground focus-visible:ring-0 ${controlClassName}`}
    />
    <datalist id={listId}>
      {searchableOptions.map((option) => <option key={option.value} value={option.label} />)}
    </datalist>
  </fieldset>;
}

export function LabeledInput({ label, className = "", showLabel = true, ...props }) {
  return <fieldset className={`relative m-0 h-12 min-w-0 rounded-md border border-input bg-background px-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 ${className}`}>
    <legend className={showLabel ? "ml-1 max-w-[88%] truncate px-1 text-[11px] font-semibold leading-none text-foreground" : "sr-only"}>{label}</legend>
    <Input data-notched-input="true" className="h-8 w-full border-0 bg-transparent px-1 py-0 text-sm shadow-none focus-visible:border-0 focus-visible:ring-0" {...props} />
  </fieldset>;
}

export function LabeledTextarea({ label, className = "", showLabel = true, ...props }) {
  return <fieldset className={`relative m-0 min-w-0 rounded-md border border-input bg-background px-2 pb-1 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 ${className}`}>
    <legend className={showLabel ? "ml-1 max-w-[88%] truncate px-1 text-[11px] font-semibold leading-none text-foreground" : "sr-only"}>{label}</legend>
    <Textarea data-notched-input="true" className="min-h-14 w-full resize-y border-0 bg-transparent px-1 py-1 text-sm shadow-none focus-visible:border-0 focus-visible:ring-0" {...props} />
  </fieldset>;
}

export function HospitalInput({ label, showLabel = true, ...props }) {
  const fieldLabel = label || props["aria-label"] || props.placeholder;
  if (!fieldLabel) return <Input {...props} />;
  return <LabeledInput label={fieldLabel} showLabel={showLabel} {...props} />;
}